import type { ReactNode } from 'react'

import './progress.css'

export interface ProgressRingProps {
  /** Что идёт: читает диктор. */
  label: string
  /** Доля сделанного, 0..1. `undefined` — доли нет (ждём ответ): дуга просто бежит по кругу, обещания «осталось столько» нет. */
  value?: number | undefined
  /** Диаметр, px. */
  size?: number
  /** Читаемое значение для диктора вместо процентов (например «Отправлено»). */
  valueText?: string
  /** Что в центре: процент, значок состояния. */
  children?: ReactNode
}

const STROKE = 6

/**
 * Кольцо хода работы («часы», как у загрузки снимка в прежнем клиенте): дуга по часовой стрелке от 12 часов, в центре
 * процент или значок. Состояние читается не только цветом: в центре цифры или значок, у кольца есть `role="progressbar"`.
 */
export function ProgressRing({ label, value, size = 72, valueText, children }: ProgressRingProps) {
  const known = value !== undefined
  const fraction = known ? Math.min(1, Math.max(0, value)) : 0
  const percent = Math.round(fraction * 100)
  const radius = (size - STROKE) / 2
  const length = 2 * Math.PI * radius
  return (
    <span
      className="krp-ring"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(known ? { 'aria-valuenow': percent } : {})}
      aria-valuetext={valueText ?? (known ? `${percent}%` : label)}
      data-indeterminate={known ? undefined : true}
      style={{ width: size, height: size }}
    >
      <svg className="krp-ring__svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle className="krp-ring__track" cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={STROKE} />
        <circle
          className="krp-ring__arc"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={known ? length : `${length * 0.28} ${length}`}
          strokeDashoffset={known ? length * (1 - fraction) : 0}
        />
      </svg>
      <span className="krp-ring__center">{children}</span>
    </span>
  )
}
